/**
 * Every API path the app knows about, in one place. Import from here instead
 * of writing URL strings at call sites, the same way `routes/paths.ts` works
 * for the router: a moved or versioned endpoint is then a one-line edit.
 *
 * Paths are relative to `API_BASE_URL`, start with a slash, and keep the
 * trailing slash the backend expects - Django redirects a missing one, and a
 * redirect drops the `Authorization` header on a cross-origin request.
 */
export const API_ENDPOINTS = {
  auth: {
    /** The Clerk-authenticated caller, as our backend sees them, plus whether
     *  it has finished provisioning their account. */
    whoami: "/api/v1/auth/whoami/",
    /**
     * The signed-in user as the app addresses them: their name, split and
     * whole, their address, and their profile picture.
     *
     * Overlaps `whoami` above and is not the same question. That one is asked
     * once, at the start of a session, and answers whether the account is ready
     * to use. This is the record behind every place the app says who is reading
     * — the rail, the greeting, the Settings heading — and is re-read whenever
     * that record changes, which is what keeps the picture in the rail the one
     * that was just saved on the General tab.
     *
     * `GET` only. It is written through `settings.profile` below: the name and
     * the photo on it are the practitioner profile's, so a `PATCH` there is
     * what this reports next.
     */
    me: "/api/v1/me/",
  },
  /**
   * The DPDP paperwork a user has to have agreed to. Read first, then written:
   * the read names the documents still outstanding for the caller and, more to
   * the point, the exact versions - so an acceptance is always recorded against
   * the text that was actually current, not one the app hard-coded.
   */
  consent: {
    /** Documents the caller has not accepted yet, current version each. */
    documents: "/api/v1/consent/documents/",
    /** Records acceptances. Answers with what it stored, hash and time included. */
    accept: "/api/v1/consent/",
  },
  onboarding: {
    /** Creates the caller's workspace and returns every membership they hold. */
    workspace: "/api/v1/onboarding/workspace/",
  },
  /**
   * The account itself, as a thing that can be ended.
   *
   * Its own group rather than a path under `settings` below: that endpoint is
   * the profile a form edits, and this one takes the workspace, its clients,
   * their plans and the whole revenue history with it. The two should not read
   * as neighbours at a call site.
   */
  account: {
    /**
     * Closes the account for good. `POST { confirmation: "DELETE MY ACCOUNT" }`
     * — an exact phrase, and the backend refuses anything else, so a request
     * that arrives by accident is refused rather than acted on.
     *
     * The phrase is a constant of the protocol and not the word the dialog asks
     * to be typed; see `CONFIRMATION` in `accountService`.
     *
     * It answers with `{ message }` and nothing else — there is no record left
     * to return. The Clerk session is still valid at that point and has to be
     * ended by the caller, which is what `AccountsTab` does before it leaves
     * for the login screen.
     */
    deletion: "/api/v1/account/deletion/",
  },
  clients: {
    /**
     * The caller's clients, paginated. Needs a completed workspace onboarding
     * on top of the bearer token - without one the backend answers 403.
     *
     * `?status=` filters the list by where each client's engagement stands:
     * `ACTIVE`, `INACTIVE` or `NEW`. Left off, every client comes back
     * whatever state they are in, which is what the screens picking a client
     * out of the whole roster ask for.
     *
     * The three are what My clients' three tabs are, one request each - the
     * split used to be made in the browser off `archived_at`, over a walk of
     * the unfiltered list. Note `ENDING_SOON` is not a value here: it is a
     * badge inside the active list rather than a list, and those clients come
     * back under `ACTIVE`.
     */
    list: "/api/v1/clients/",
    /**
     * Creates one. The reference fields go up as the ids the catalog lists
     * carry - `title: 2`, not `title: "Mrs"` - and come back as the whole
     * `{ id, name }` object, so the response is a client record like any
     * other. `place` is the state here too.
     *
     * 400 is `{ code: "validation_failed", message, details }`; a 404 names
     * the list the id was not in - `title_not_found`, `goal_not_found`,
     * `life_style_not_found`, `dietary_pattern_not_found`, `allergen_not_found`.
     */
    create: "/api/v1/clients/add/",
    /**
     * Edits one. `PATCH`, JSON, the same shape the create above takes.
     *
     * A partial update: only the fields sent are touched. `goals` and
     * `food_allergies` are the exception to that being additive - each
     * replaces the client's whole list, so it goes up as every id they should
     * end with, a plain JSON array either way - `[]` clears it.
     *
     * It answers with the whole record, in the same shape the reads return, so
     * the response maps like any other client.
     *
     * 400 is `validation_failed`; 404 is `client_not_found`, the same as the
     * detail read.
     */
    update: (publicId: string) =>
      `/api/v1/clients/update/${encodeURIComponent(publicId)}/`,
    /**
     * One client, by the `public_id` the list returns. Field for field the
     * same shape as a row of that list - `place` holding the state included -
     * so both go through one mapping. Same 403 without a completed onboarding.
     *
     * It also carries `payment_request`: the plan the client is on, as the
     * request that sold it, or `null` for one who has never been asked for
     * money. That is where the Details tab's Plan card comes from - what was
     * bought, for how much, and the day it was paid. The history of every
     * request since is `paymentRequests` below.
     *
     * An id that is not the caller's, or not a client at all, is a 404 with
     * `{ code: "client_not_found", message: "Client not found." }`; the API
     * client lifts that `message` onto the `ApiError` it throws.
     *
     * A builder rather than a string, the way `clientDetailPath` builds the
     * route - so no call site interpolates an id into a URL by hand.
     */
    detail: (publicId: string) =>
      `/api/v1/clients/${encodeURIComponent(publicId)}/`,
    /**
     * What this client has been asked to pay, and what came of each ask -
     * the Payments tab's whole history, and the way it raises a new request.
     *
     * `GET` lists them in DRF's page envelope, `page` and `page_size` as
     * usual. A row carries the invoice number, the plan as it was sold
     * (name, duration label, list price, the discount the charge worked out
     * to), the state the request is in, and - once there is one - the payment
     * received against it and the engagement it opened.
     *
     * `POST` raises one from `{ service, amount_due }`: the plan as the
     * `public_id` its own list carries, the amount as the decimal string the
     * backend keeps money in - `"2000.00"`, not `2000`. An optional
     * `start_date` dates the engagement the payment would open; left off, the
     * backend decides it. Everything else on the row - the invoice number, the
     * client's own details, the discount - is worked out by the backend and
     * comes back on the response, which is a row of the list above.
     */
    paymentRequests: (publicId: string) =>
      `/api/v1/clients/${encodeURIComponent(publicId)}/payment-requests/`,
  },
  /**
   * The enquiries the practice has taken - people who have asked about a
   * plan but are not clients yet.
   *
   * Note the spelling: the backend routes them under `inquiries`, the app
   * calls them enquiries everywhere else. The difference stops here, which is
   * the point of this file.
   */
  enquiries: {
    /**
     * `GET` lists them, newest first, in DRF's page envelope. `search`
     * matches a name; `page` (1-based) and `page_size` cut the rest into the
     * pages the table shows, so the screen holds no more rows than it draws.
     *
     * A row carries only what the list needs - the name, the goal as a whole
     * `{ id, name }`, how the enquiry arrived, when, and the stage it is at -
     * plus `days_ago` already counted, so the column is the server's answer
     * rather than one worked out from a clock that may not agree with it.
     */
    list: "/api/v1/inquiries/",
    /**
     * Creates one, and answers with the whole record in the same shape
     * `detail` below returns - so what comes back is an enquiry like any
     * other, `public_id` and `JUST_ADDED` included.
     *
     * JSON, not `multipart/form-data`: every field goes up as a plain body,
     * which is what the backend parses here. The reference fields go as the
     * ids their catalog lists carry - `title: 1`, not `title: "Dr"` - and come
     * back expanded to `{ id, name }`. `goals` is a list, since an enquiry may
     * be raised against more than one - a real JSON array, `goals: [1, 3]`.
     * The two numbers go up as the ten digits the form collects and come back
     * in E.164.
     */
    create: "/api/v1/inquiries/add/",
    /**
     * Edits one. `PATCH`, JSON, the same fields `create` takes - and it
     * answers with the whole record, so what comes back is what the detail
     * screen reads next.
     *
     * A path of its own rather than a verb on `detail` below, which is how the
     * backend has it: `/update/` sits before the id, not after.
     *
     * A partial body is a partial edit, which is what moving a stage sends:
     * `status` alone - `JUST_ADDED`, `INTERESTED`, `NOT_INTERESTED`,
     * `PAYMENT_SENT` - leaves every other field of the enquiry as it was. See
     * `updateEnquiryStatus`.
     *
     * The response carries no `days_ago` - the one field of a detail read that
     * is missing here - so `EnquiryDetailResponse` has it optional and the
     * screens count from `created_at` when it is not there.
     */
    update: (publicId: string) =>
      `/api/v1/inquiries/update/${encodeURIComponent(publicId)}/`,
    /**
     * One enquiry, whole, by the `public_id` a listed row carries: the fields
     * of that row plus everything the Add enquiry form collects - title,
     * gender, date of birth, lifestyle, the address and the ways to reach
     * them.
     *
     * And the payment raised against it, embedded as `payment_request` - the
     * same invoice the `POST` below answers with, or `null` until there is
     * one. That is what the detail screen draws its amount, its pill and its
     * Confirm payment from, so a requested payment survives a reload rather
     * than living only in the tab that raised it.
     *
     * A `public_id` that is not the caller's, or not an enquiry at all, is a
     * 404; `useEnquiry` branches on the status to tell a missing enquiry from
     * a failed request.
     *
     * A builder rather than a string, the way `clients.detail` is - so no call
     * site interpolates an id into a URL by hand.
     */
    detail: (publicId: string) =>
      `/api/v1/inquiries/${encodeURIComponent(publicId)}/`,
    /**
     * Asks this enquiry for money — the call behind the detail screen's
     * Request payment.
     *
     * `POST` with the same `{ service, amount_due }` body a client's own
     * payment request takes, and it answers with the same invoice: the record
     * is one shape whichever it was raised against, and its `source` says
     * which - `INQUIRY` here, where a client's reads `CLIENT`. See
     * `lib/api/paymentRequests`.
     *
     * There is no `GET` beside it, and none needed: an enquiry is asked for
     * money once — the request is what turns it into a client — so the one
     * request it has comes back on `detail` above, and the history of
     * everything since belongs to the client it became.
     */
    paymentRequests: (publicId: string) =>
      `/api/v1/inquiries/${encodeURIComponent(publicId)}/payment-requests/`,
    /**
     * The form behind a practitioner's shared link, and the enquiry it raises.
     *
     * Both are public: the name in the path is what resolves the workspace,
     * and there is no session to send. Nothing here takes a bearer token —
     * `publicEnquiryService` deliberately makes these calls without one.
     *
     * The `GET` fills the whole screen in one call:
     *
     *     {
     *       "workspace": { "brand_name": …, "logo": { "url": …, … } | null },
     *       "goals":       [{ "id": 23, "name": "Clinical Nutrition" }, …],
     *       "life_styles": [{ "id": 8, "name": "Sedentary",
     *                         "definition": "Mostly sitting/lying, …" }, …]
     *     }
     *
     * `workspace` is the practitioner's name and logo and nothing else — the
     * endpoint is open, so anything more would be public. The two catalogs come
     * down with it rather than from the signed-in `/api/v1/goals/` and
     * `/api/v1/life_styles/`, which is what lets a form with no session behind
     * it draw both dropdowns; the ids in them are what the `POST` takes back.
     *
     * `logo.url` is a path rooted at the API's origin, not at the host serving
     * the form — `mediaUrl` is what resolves it. See `publicEnquiryService`.
     *
     * The `POST` raises the enquiry; what it answers with is not read, because
     * the record belongs to the practitioner and the person who filled the form
     * has no business seeing it back.
     *
     * `handle` is the practitioner's own name — `priya-nutrition` — not an id.
     * A name nobody has claimed is a 404, which the screen shows as "this form
     * is not available".
     */
    publicForm: (handle: string) =>
      `/api/v1/public/enquiry-form/${encodeURIComponent(handle)}/`,
    publicSubmit: (handle: string) =>
      `/api/v1/public/enquiry-form/${encodeURIComponent(handle)}/submit/`,
    /**
     * Whether this practitioner already holds an enquiry from a contact, asked
     * before the form is sent rather than after.
     *
     * One of `?email=` or `?phone_number=` per call — the form checks the two
     * fields separately so the answer lands under the field it is about, and a
     * combined call could only say "one of these" without saying which.
     *
     *     { "already_registered": true,
     *       "message": "An enquiry with this contact is already registered." }
     *
     * `message` is what the field shows; `already_registered` alone is what it
     * is keyed on, so a response with no message still reddens the control.
     *
     * Open, like the two calls above it — no bearer token, the slug resolves
     * the workspace. That openness is the thing to be careful with: this
     * answers a yes/no question about a stranger's contact details, so it is
     * asked only about a value somebody typed into their own form, only once
     * that value is well-formed, and never in a loop over a list. It is also
     * why a failure here is swallowed rather than shown — see
     * `usePublicContactCheck`.
     */
    publicCheckContact: (handle: string) =>
      `/api/v1/public/enquiry-form/${encodeURIComponent(handle)}/check-contact/`,
    /**
     * The address a practitioner's forms answer at, which the My brand
     * screen's Form tab reads and claims.
     *
     * A record of its own rather than a field on the brand profile, because
     * it is not the same kind of thing: every field there is the workspace's
     * own to overwrite, and the slug is taken out of a namespace shared with
     * every other workspace.
     *
     * `GET` answers `{ connect_link: { slug, is_active, share_url } }`, or a
     * 404 where none has been claimed. `PATCH` takes `{ slug }` and answers
     * with the same record.
     *
     * `share_url` is the whole address, built by the backend - which is what
     * makes it worth having. The server knows where it is actually serving
     * from; this app can only guess, and a link copied out of a guess is a
     * link that goes nowhere. See `lib/site/config`.
     */
    connectLink: "/api/v1/enquiry-connect-link/",
    /**
     * Whether a slug is free: `?slug=ayushi-sahu` answers
     * `{ slug, available }`.
     *
     * A courtesy rather than a guarantee. It is answered before the claim is
     * sent, so the honest answer can still be stale by the time it is - the
     * `PATCH` above is what actually decides, and a rejection from it is
     * handled whatever this said.
     */
    connectLinkAvailability: "/api/v1/enquiry-connect-link/availability/",
  },
  /**
   * The pre-consultation form: the health details one named client fills in
   * before their first consultation.
   *
   * Public, like the enquiry form above and for the same reason — the person
   * filling it in has no Naurist account and never will — but reached a
   * different way. An enquiry form is opened by anybody who has the
   * practitioner's public name; this is opened by one person, once, through a
   * token the backend minted for them:
   *
   *     https://<practitioner>.naurist.com/i/<token>
   *
   * The `/i/` segment is what keeps the two apart on one host. Without it
   * every first path segment on that subdomain is a practitioner's name, and a
   * token would be read as somebody's handle.
   *
   * The token is the whole of the authorisation. It is long, random and
   * single-use, and it is never shown to anyone but the client it was made
   * for — so nothing here takes an id, and nothing here may be made guessable.
   *
   * `createForm` and `practiceDomain` are the exceptions to all of the above:
   * they are the practitioner's own calls, behind their bearer token — one
   * raises a form for somebody, the other says which subdomain the resulting
   * links are served on. Neither is reachable by the person the link was made
   * for; only the two above are.
   */
  preconsultation: {
    /**
     * What the form should draw: the practitioner's workspace to head the page
     * with, the catalogs its dropdowns offer, whatever the workspace already
     * knows about the person — their name, their number, the goals they were
     * signed up against — and `field_config`, which says which of the optional
     * sections this practitioner asks at all.
     *
     * The slug as well as the token, because the link is one address and this
     * is the read behind it: `ayushisahuu.naurist.com/i/<token>` is served by
     * `/api/v1/public/pre-consultation/ayushisahuu/<token>/`. The slug is the
     * one in the address bar — see `formHandleFromHost` — and it is the
     * pre-consultation service's own record of the domain, which is what
     * `practiceDomain` below writes. A token read under the wrong slug is a
     * 404 like any other.
     *
     * The catalogs arrive at the top level rather than under a `catalogs` key,
     * and there is no catalog of professions among them — that field falls
     * back to the form's own list and goes up as text. Every other list falls
     * back too where it is not served. See `PRECONSULTATION_FALLBACK_OPTIONS`.
     *
     * No state comes back. A link that has been spent, has run out, or never
     * existed is a 404 carrying `pre_consultation_form_not_found` — one answer
     * for all three, which is the right way round: anybody can paste a string
     * into a browser, and an answer that distinguished them would confirm to a
     * stranger that a link was once real.
     */
    form: (handle: string, token: string) =>
      `/api/v1/public/pre-consultation/${encodeURIComponent(
        handle
      )}/${encodeURIComponent(token)}/`,
    /**
     * The answers. `POST`, and it spends the token — a second submission
     * against the same one is refused, which is what "single use" means.
     *
     * The same slug-and-token pair the read is made with.
     *
     * What comes back is the whole form record, `SUBMITTED`, carrying the
     * answers as they were stored. No screen draws it — the details belong to
     * the practitioner's record of that client, and the person who filled the
     * form gets a thank-you and nothing to return to — but it is read into the
     * same shape a practitioner's side reads a form in. See
     * `submitPreconsultation`.
     *
     * Measurements go up with the unit beside them (`68` and `KG`) rather than
     * converted to one canonical unit here. The practitioner reads what their
     * client actually said, and a conversion done in a browser is a rounding
     * error nobody can trace back to a keystroke.
     */
    submit: (handle: string, token: string) =>
      `/api/v1/public/pre-consultation/${encodeURIComponent(
        handle
      )}/${encodeURIComponent(token)}/submit/`,
    /**
     * Every form this practitioner has raised, for reading rather than
     * writing — which of them have come back, and who from.
     *
     * The collection `createForm` below hangs off, and the source the flags on
     * My clients are drawn from: a form that has been answered is news, and
     * news about forms belongs to the forms endpoint rather than smuggled
     * inside every row of every page of the client list.
     *
     * Read defensively, and its absence is not an error. This is the one route
     * here that was inferred rather than documented: `forms/` resolves and
     * answers 401 unauthenticated with `Allow: GET, HEAD, OPTIONS`, so the
     * collection is there and serves a GET — but in what shape is the server's
     * to say. `fetchWaitingSubmissions` takes whatever comes and gives up
     * quietly on anything it cannot read, which leaves the screens exactly as
     * they were before it was asked.
     */
    forms: "/api/v1/pre-consultation/forms/",
    /**
     * Raises a pre-consultation form for one person, and answers with it.
     *
     * `POST` with exactly one of `{ client_id }` or `{ inquiry_id }` — the
     * `public_id` of whoever it is for. Two ids because the same form is put
     * to somebody at either end of the funnel: an enquiry who has not bought
     * anything yet, and a client who has. Which one was sent comes back on
     * `subject`, named — `{ type, public_id, name }`.
     *
     * The response is the form record itself, and it is mostly empty: every
     * answer field comes back blank or `null`, because nobody has filled it in
     * yet. What is actually read off it is `share_url` — the whole address,
     * built by the server, with `share_token` already in the path — and
     * `expires_at`, the moment the link stops working. `status` is `PENDING`
     * until the answers come back.
     *
     * Nothing is sent anywhere by this call. The practitioner is handed the
     * address to pass on: they know which of the two numbers on the record
     * their client actually reads, and a message from a person is opened where
     * one from a system is not.
     *
     * `share_url` is the address the two public calls above are then reached
     * at, which is why nothing on this side ever assembles one — see
     * `practiceDomain` for how the host in it comes to be known.
     */
    createForm: "/api/v1/pre-consultation/forms/add/",
    /**
     * Takes a submitted form's answers onto the record they were asked for.
     * `POST`, no body, and it answers with the whole form as it now stands -
     * `APPROVED`, carrying the `reviewed_at` it was accepted at.
     *
     * The merge is the backend's: the answers land on the client, which is what
     * fills the height and the weight and the rest of the Details tab. Nothing
     * here assembles that - the response is read only for where the form now
     * stands, and the client is re-read for what it now holds.
     */
    approveForm: (publicId: string) =>
      `/api/v1/pre-consultation/forms/${encodeURIComponent(publicId)}/approve/`,
    /**
     * Sends a submitted form back to be fixed. `POST { reason }` - what the
     * client is to put right - and it answers with the form as `REJECTED`.
     *
     * Nothing is deleted: the answers stay on the form, and the record they
     * were asked for is left as it was. A rejection is the practitioner saying
     * these are not right yet, not the form being thrown away.
     */
    rejectForm: (publicId: string) =>
      `/api/v1/pre-consultation/forms/${encodeURIComponent(publicId)}/reject/`,
    /**
     * Registers the subdomain this workspace's pre-consultation links are to
     * be served on. `PATCH { slug }`, and it answers `{ slug }` — the name as
     * the service stored it.
     *
     * The same slug the practitioner claims at `enquiryConnectLink`, sent on
     * to the service that mints the tokens: one name, held by two services,
     * because it is one address. `https://ayushisahuu.naurist.com/form` is
     * their enquiry form and `https://ayushisahuu.naurist.com/i/<token>` is a
     * client's pre-consultation form, and a service that had never been told
     * the name could not build the second.
     *
     * So it goes up with the claim — see `saveConnectLink`, which is where the
     * pair is sent, and `useFormLink`, which is where the order is decided:
     * the slug `enquiryConnectLink` stored, never the one that was typed.
     *
     * Behind the practitioner's own bearer token, unlike everything above it
     * here. It writes what the whole subdomain resolves to, so it is not
     * something a link's holder may touch.
     */
    practiceDomain: "/api/v1/pre-consultation/practice-domain/",
  },
  /**
   * A payment request once it exists, whoever it was raised against.
   *
   * Both paths above create one under its parent — a client, or an enquiry —
   * but what happens to it afterwards is the request's own business, so these
   * hang off the request's `public_id` at the top level rather than off the
   * record it was raised from. One pair of calls then serves both screens.
   */
  paymentRequests: {
    /**
     * Marks the money in. `POST` with `{ received_on }` — the day it arrived,
     * as an ISO date, which is the one thing the backend cannot know: nothing
     * reports a UPI transfer or an envelope of cash back to it.
     *
     * It answers with the receipt alone — `{ public_id, paid_amount,
     * currency, received_on }` — not the whole request, so a caller holding
     * the row marks it paid from what came back rather than replacing it.
     */
    confirm: (publicId: string) =>
      `/api/v1/payment-requests/${encodeURIComponent(publicId)}/confirm/`,
    /**
     * Calls a raised request off. `POST` with no body, and it answers with the
     * whole request as it now stands — `CANCELLED`, and carrying the
     * `cancelled_at` it was called off at.
     *
     * The request stays on the backend rather than being deleted, which is
     * what lets the history keep it: what was asked for and withdrawn is part
     * of what passed between the practice and the client.
     */
    cancel: (publicId: string) =>
      `/api/v1/payment-requests/${encodeURIComponent(publicId)}/cancel/`,
  },
  /**
   * Every payment request the workspace has raised, whoever it was raised
   * against, as the Revenue screen lists them.
   *
   * A row is the same `PaymentRequestResponse` the two paths above answer with
   * — this is the workspace-wide list of them rather than one record's history,
   * which is why it hangs off nothing. Paginated like the rest, and it carries
   * a `summary` beside the page: what has come in, what is still out and what
   * was called off, across everything it holds rather than across the page.
   */
  revenue: {
    list: "/api/v1/revenue/",
  },
  /**
   * What Naurist sells, before anybody has bought it.
   *
   * The shop window, which is why it is its own group and not a path under
   * `subscriptions` below: everything there is about one workspace's own
   * mandate, and this is the same price list whoever asks.
   */
  billing: {
    /**
     * Every plan on sale, one row per billing cycle:
     *
     *   { code: "PRO_YEARLY", name: "Naurist Pro", interval: "YEAR",
     *     amount: "4788.00", list_amount: "9588.00",
     *     monthly_equivalent: "399.00", currency: "INR" }
     *
     * A bare array rather than a paginated envelope — there are two rows and
     * there is no page to ask for.
     *
     * `amount` and `list_amount` are for the whole term the cycle covers: a
     * year of the yearly plan, a month of the monthly one. `monthly_equivalent`
     * is that first figure divided down to a month, and is the only one the two
     * cycles can be compared on, which is what the cards quote. Money is a
     * decimal string, as everywhere else the backend quotes it.
     *
     * This is the one place prices come from. Nothing in the app writes a
     * figure down — see `plansService`, which is where these become the shapes
     * the Billing screens draw.
     */
    plans: "/api/v1/billing/plans/",
    /**
     * Who the subscription is invoiced to — the one record behind the form on
     * the checkout, and the only party it involves is the workspace itself, so
     * nothing here is keyed by an id: the bearer token says whose it is.
     *
     * `GET` answers `{ billing_profile: { … } }`, or nothing inside that
     * envelope for a workspace that has never filled it in — an ordinary state,
     * not a failure, and the checkout simply opens the form on what it can
     * gather from `My brand` instead.
     *
     * `PATCH` takes the record bare — no envelope on the way up — and answers
     * with the same envelope the `GET` does. Every field goes up together
     * because the form is submitted whole; there is no half-saved address.
     *
     * `phone_number` is E.164 (`+919876543210`) and `country` is an ISO-3166
     * pair; the form holds ten digits and no country at all, and
     * `billingProfileService` is where that is translated both ways.
     */
    profile: "/api/v1/billing/profile/",
    /**
     * What a plan comes to today, priced by the backend.
     *
     * `POST { plan_code }` — the code off `plans` above, and nothing else. It
     * answers with the whole breakdown as decimal strings: the base, any
     * discount, what is taxable, the tax split three ways (`cgst`/`sgst` where
     * the supply is intra-state, `igst` where it is not), the rate, and the
     * total.
     *
     * The browser works out none of this, and that is the point: the place of
     * supply comes off the billing profile above rather than off anything typed
     * on the page, and the figure quoted under Pay now is the figure that will
     * be charged.
     *
     * **No coupon goes up.** The request used to carry `coupon_code` and no
     * longer does, so a code typed on the checkout reaches nobody. The
     * *response* still carries one, which is the only way a discount can appear
     * on an order now: because the server put it there.
     */
    quote: "/api/v1/billing/quote/",
    /**
     * Buying it. `POST { plan_code }` — the same value the quote above is
     * asked, because it is the same order; who it is billed to is the profile,
     * already recorded, and not repeated here, and no coupon travels for the
     * reason set out on `quote`.
     *
     * It answers with three things:
     *
     *   - `subscription` — the record, `PENDING_AUTH`: issued, no mandate
     *     signed, nothing charged.
     *   - `checkout` — what the gateway's window needs: the *public* `key` and
     *     the `subscription_id` the mandate is signed against. If a `key_secret`
     *     ever appears here, that is an incident and not a field to map.
     *   - `quote` — the price it was actually bought at, in the same shape the
     *     quote endpoint answers in. Worth having: it is the server's last word
     *     on what this costs, taken at the moment of purchase rather than at the
     *     moment the page was opened.
     *
     * Creating a subscription needs the key secret, which is why this is our
     * backend's job and not the browser's, and why nothing here is trusted from
     * the page: the plan is named by code and priced again on the way through.
     *
     * `POST` only. Reading one back is `currentSubscription` below, which is a
     * **different path** — mind the plural.
     */
    subscriptions: "/api/v1/billing/subscriptions/",
    /**
     * What the workspace may do today, and why. Four things in one answer:
     *
     *   - `subscription` — the plan it is on, carried whole, or `null`.
     *   - `trial` — `{ started_at, ends_at, is_active }`, the fortnight and
     *     whether it is still running. Present whether or not it is.
     *   - `access_source` — `"SUBSCRIPTION"`, `"FREE_TRIAL"` or `"NONE"`:
     *     what is holding the door open. **This is the access decision** —
     *     every locked button in the app reads it and nothing else, not the
     *     subscription's status; see `hasAccess`.
     *   - `can_start_trial` — whether the free trial is still there to be
     *     taken. **This is what decides whether a new account is shown the
     *     trial flow or the app**, and it is the server's decision rather than
     *     something the browser works out from the other three; see
     *     `fetchTrialAccess`.
     *
     * The one call that answers "am I a paying customer", what the Plan details
     * screen draws its status card from, and what stands between signing up and
     * the dashboard.
     *
     * Note the path: `subscription`, singular, where the one above it is
     * `subscriptions`. Two endpoints that differ by one letter is exactly the
     * mistake this file exists to stop being made at a call site, which is why
     * they are named apart here rather than by their URLs.
     */
    currentSubscription: "/api/v1/billing/subscription/",
    /**
     * Stopping it renewing. `POST` with nothing in the body at all — there is
     * one subscription per workspace and the bearer token says whose, which is
     * why this hangs off `subscription` above rather than off an id.
     *
     * There is no `at_cycle_end` to send, and that is the whole policy rather
     * than an omission: cancelling turns auto-renewal off and leaves the cycle
     * already paid for running to its end. Ending a plan somebody has paid for
     * is not a thing this offers, so it is not a flag the browser gets to set —
     * see `CancelSubscriptionModal`, which is where that promise is made.
     *
     * It answers `{ subscription }` — the same record and the same envelope
     * `currentSubscription` reads, so the screen redraws from what the backend
     * said rather than from what the browser assumed. Note what comes back:
     * `status` is still `ACTIVE` and `current_period_end` is unmoved, with
     * `cancel_requested_at` newly set. **That timestamp is the whole of the
     * news.** A record read for its status alone looks untouched, which is why
     * `cancelledOn` is what the screens branch on — see `cancellable` in
     * `BillingSection` and `renews` on `SubscriptionStatusCard`.
     *
     * The plan turns `CANCELLED` on the day the cycle runs out, and that is the
     * backend's to do when the gateway says so.
     */
    cancelSubscription: "/api/v1/billing/subscription/cancel/",
    /**
     * Starting the free trial. `POST` with nothing in the body — the bearer
     * token says whose it is, and there is no plan to name: the trial is the
     * product, not a cycle of it.
     *
     * It answers `{ trial: { started_at, ends_at, is_active } }`, and those
     * dates are the trial's. Nothing on this side works out when a trial ends
     * any more; see `startTrial`.
     *
     * Who the invoice at the end of it will be headed with is *not* sent here.
     * That is the billing profile, recorded through `profile` above before this
     * is called — the same record and the same call the paid checkout uses.
     */
    trial: "/api/v1/billing/trial/",
    /**
     * Every invoice the workspace has been raised, in DRF's page envelope —
     * `{ count, next, previous, results }`.
     *
     * What the Plan details screen's history table lists. An empty `results` is
     * an ordinary answer and not an empty state to apologise for: a plan bought
     * minutes ago has no invoice against it until the first charge settles.
     *
     * A row is the whole of a charge: the number as printed, the plan it was
     * for, the figures the quote worked out (base, discount, taxable, the tax
     * split three ways, total), the period it bought — and, once there is one,
     * the `payment` that settled it, method and all. `status` is the invoice's
     * own — `PAID` — which is a different question from the one the table's
     * column asks; see `toInvoiceStatus`.
     */
    invoices: "/api/v1/billing/invoices/",
    /**
     * One invoice as the document it is, by the `public_id` its row in
     * `invoices` above carries. `GET` answers with the PDF itself — the bytes,
     * not a link to them — so it is read with `api.getBlob` and an `Accept` of
     * `application/pdf` followed by a wildcard, the same way a meal plan's
     * document is. The wildcard is not decoration: the view writes the file
     * itself rather than through a renderer, so DRF negotiates a bare
     * `application/pdf` away as a 406 before the view is ever reached. See
     * `fetchInvoiceDocument`, which is where the header is written out.
     *
     * The backend renders it because the backend is what holds the invoice
     * number, the tax split and the registered details it has to carry. An
     * invoice drawn in the browser would be a second document for one payment,
     * and only one of them can be the record.
     */
    invoicePdf: (publicId: string) =>
      `/api/v1/billing/invoices/${encodeURIComponent(publicId)}/pdf/`,
    /**
     * The seven-day money back: whether it can still be had, and the asking for
     * it. One path, two verbs, and no id on either — there is one refundable
     * payment per workspace and the bearer token says whose.
     *
     * `GET` answers the whole panel in a single call, which is the point of it:
     *
     *   - `eligible` — whether to draw the Request button at all. **This is the
     *     answer**, not a hint to be second-guessed: the browser used to work
     *     the same thing out from the invoice list and its own clock, and a
     *     screen that offers a refund the server will refuse is worse than one
     *     that offers nothing.
     *   - `reason` — the server's own word for why, `"refundable"` or the
     *     grounds for a no. Diagnostic: it is a code for a log, not copy, and
     *     nothing on the screen prints it.
     *   - `invoice` — the payment that would go back: `public_id`,
     *     `invoice_number`, `payment_date`, `amount`, `currency`. What the
     *     confirm dialog quotes, so the figure in front of the button is the
     *     server's and not the newest row of a table that happened to load.
     *   - `window_ends_at` — when the offer shuts, as a full timestamp rather
     *     than a bare day. What dates the card while it is open and what dates
     *     the lapsed note on `CancelPlanCard` once it is not.
     *   - `reason_options` — `{ code, label }`, and the dropdown is built from
     *     exactly this. The codes are the backend's vocabulary; a list shipped
     *     in the bundle would drift out of step with it the first time somebody
     *     added a row.
     *   - `request` — the refund already asked for, or `null`. Carries its
     *     `status`, the figure, the invoice it is against and `processed_at`
     *     once the money has gone. This is what puts the screen into the refund
     *     states; the subscription record has nothing to say about one.
     *
     * `POST { reason_code }` asks for it, answering `{ request }` — the same
     * record shape, newly `REQUESTED`. The code must be one the `GET` offered.
     *
     * The reason is asked for and not required, so a request with nothing
     * picked goes up with no `reason_code` at all: the card promises no
     * questions asked, and a field that blocks the refund would make that a lie.
     * See `requestRefund`.
     *
     * Whether the window is open is decided here as well as drawn from here. A
     * request that arrives after it has shut is refused by the backend, because
     * a browser's idea of the date is not what a refund can turn on.
     */
    refund: "/api/v1/billing/refund/",
  },
  /**
   * The workspace's own subscription to Naurist — what the Billing screen
   * sells and what the checkout buys. Not to be confused with anything a
   * dietitian's clients pay for: those are `paymentRequests` above, one-off
   * and raised by hand. This is the recurring mandate the practice signs with
   * us, and the only party it involves is the workspace itself.
   *
   * Which is why nothing here hangs off a client id. There is one subscription
   * per workspace, and the bearer token says which workspace.
   *
   * The gateway is Razorpay Subscriptions. Two rules follow from that and they
   * are the backend's, not this app's:
   *
   *   - The `key_secret` never reaches the browser. Creating a subscription,
   *     verifying a signature and receiving a webhook are all server-side. The
   *     only Razorpay value this app is ever given is the public `key_id`, on
   *     the create response, because the SDK in the page needs it.
   *   - The webhook is what decides a subscription's state, not anything the
   *     browser reports. A mandate can succeed after the tab is closed, so
   *     every screen here reads the state back from `detail` rather than
   *     trusting what the SDK handed it. See `verify`.
   */
  subscriptions: {
    /**
     * Starts one. `POST` with the plan cycle and the billing details the
     * checkout collected — who the invoice is to, where they are, and their
     * GSTIN where they gave one.
     *
     * It answers with the subscription as this app reads it, plus the two
     * values the SDK needs to authorise it: `razorpay_subscription_id` and the
     * public `razorpay_key_id`. The record is `CREATED` at this point — no
     * mandate has been signed and no money has moved.
     */
    create: "/api/v1/subscriptions/",
    /**
     * What the workspace is on right now, or `null` for one that has never
     * subscribed. What the Billing screen draws its status card from, and the
     * one call that answers "am I a paying customer" for the whole app.
     */
    current: "/api/v1/subscriptions/current/",
    /**
     * One subscription, by the `public_id` the create answered with. This is
     * the endpoint the checkout polls while a mandate is being approved, and
     * the one a result screen confirms against — the browser's own account of
     * what happened is a hint, and this is the answer.
     *
     * A builder rather than a string, the way `clients.detail` is - so no call
     * site interpolates an id into a URL by hand.
     */
    detail: (publicId: string) =>
      `/api/v1/subscriptions/${encodeURIComponent(publicId)}/`,
    /**
     * Hands the gateway's own account of the authorisation to the backend to
     * be checked: `{ razorpay_payment_id, razorpay_signature }`.
     *
     * The signature is an HMAC the backend recomputes with the secret. Note
     * the payload it is computed over — for a subscription it is
     * `razorpay_payment_id + "|" + subscription_id`, which is the reverse of
     * the order an ordinary order uses. Getting it the wrong way round rejects
     * every payment, and does it silently.
     *
     * It answers with the subscription as it now stands. Calling it is an
     * optimisation, not the mechanism: the webhook activates the subscription
     * whether or not the browser ever gets here.
     */
    verify: (publicId: string) =>
      `/api/v1/subscriptions/${encodeURIComponent(publicId)}/verify/`,
    // Stopping it renewing used to be here, keyed by the subscription's id and
    // taking an `at_cycle_end` the browser had to set. It is
    // `billing.cancelSubscription` above now — no id, no body, and the promise
    // that the paid cycle runs out is the backend's policy rather than a flag.
    // The seven-day money back used to be here too, keyed by the subscription's
    // id. It is `billing.refund` above now — one path, no id, and a `GET` that
    // answers the whole panel rather than a `POST` the screen had to work out
    // for itself whether to offer.
    /**
     * The invoice for the cycle just paid for. `GET` answers with the PDF
     * itself — the bytes, not a link to them — so it is read with
     * `api.getBlob` and an `Accept` of `application/pdf`, the same way a meal
     * plan's document is.
     *
     * The backend renders it because the backend is what knows the invoice
     * number, the tax breakdown and the registered details it has to carry. An
     * invoice built in the browser would be a different document from the one
     * emailed on renewal, and only one of them can be the record.
     */
    invoice: (publicId: string) =>
      `/api/v1/subscriptions/${encodeURIComponent(publicId)}/invoice/`,
    /**
     * Every invoice the workspace has been raised, newest first — what the
     * Billing screen's history table lists.
     *
     * The caller's own, like `clients.list`: there is no id in the path
     * because the token says whose they are. It spans subscriptions rather
     * than hanging off one, so a practice that cancelled a monthly plan and
     * later took a yearly one still reads as one history — which is what
     * anybody reconciling a year of payments is looking for.
     */
    invoices: "/api/v1/subscriptions/invoices/",
    /**
     * One invoice's PDF, by the `public_id` the list carries. `GET` answers
     * with the bytes, the same as `invoice` above — this is the row's own
     * download, where that one is the latest cycle's.
     */
    invoiceDocument: (publicId: string) =>
      `/api/v1/subscriptions/invoices/${encodeURIComponent(publicId)}/document/`,
  },
  dashboard: {
    /**
     * Everything the dashboard opens with, in one call: how many clients the
     * workspace has, the newest few, a count per month for the current year,
     * and the plan figures.
     *
     * The two plan counts come back `null` while the backend has nothing to
     * count them from, so they are "no figure yet" rather than zero - see
     * `DashboardResponse`.
     */
    summary: "/api/v1/dashboard/",
  },
  /**
   * The dietitian's own setup, as the My brand screen edits it. Two records
   * rather than one - the brand the documents are headed with, and the details
   * they are to be paid by - so saving the screen is two `PATCH`es.
   *
   * Both are partial updates of the caller's single record: there is no id in
   * the path, because the workspace is the record.
   */
  brand: {
    /** Name, contact details, address, and the `public_id` of the logo. */
    profile: "/api/v1/brand/",
    /** Bank and UPI details, and the `public_id` of the payment QR. */
    paymentConfiguration: "/api/v1/payment-configuration/",
  },
  /**
   * The two documents a practice attaches to every plan it sends - the
   * guidelines a client is to follow, and the terms a plan is sold under -
   * as the My brand screen's Guidelines & policy tab edits them.
   *
   * One contract, two documents: each has a `GET`, a `PUT` and a `reset/`,
   * and every one of the six answers with the same body -
   * `{ document_type, body, resolved_body, clauses, version, is_custom }`.
   * `body` is the HTML the editor loads back; `resolved_body` is the same
   * with the policy's `{{clause:N}}` tokens replaced by their sentences, and
   * is what a client reads. `clauses` is always a list, `[]` where there
   * are none. `is_custom: false` with `version: 0` is the system default,
   * never published over; the first publish makes version `1`.
   *
   * A `PUT` is a whole publish, never a patch: it supersedes the version
   * before it and inserts the next, and takes the complete `body` every
   * time. The server sanitises it on the way in to a short allow-list -
   * `h1`-`h4`, `p`, `ul`/`ol`/`li`, `strong`/`b`, `em`/`i`, `u`, `br`, and a
   * `span` with only `color` and `background-color` in its `style` - and
   * strips the rest without saying so. `lib/richText` keeps the same list.
   *
   * Errors carry a `code` to branch on: `clause_token_mismatch`,
   * `guidelines_cannot_have_clauses`, `unknown_package_duration`.
   */
  practiceDocuments: {
    /**
     * `GET` answers the current guidelines - custom, or the system default.
     * `PUT` publishes a new version from `{ body }` and nothing else: a
     * `clauses` key, even an empty one, is refused with
     * `guidelines_cannot_have_clauses`.
     */
    guidelines: "/api/v1/guidelines/",
    /**
     * `POST`, no body. Puts the system default back and answers with it -
     * `200` even when already on it, which is a fair thing to ask for again.
     * Costs no version number: the next publish carries on from the highest
     * the guidelines ever reached.
     */
    guidelinesReset: "/api/v1/guidelines/reset/",
    /**
     * `GET` answers the current policy. `PUT` publishes a new version from
     * `{ body, clauses }` - `clauses` optional, each one
     * `{ label, package_duration_id, position }` with the duration's id
     * from `catalog.planDurations`. The body's `{{clause:N}}` tokens and the
     * list must match one to one - every token a clause at `position: N`,
     * every clause a token, no position twice - or the whole publish is
     * refused with `clause_token_mismatch` (`details.body` or
     * `details.clauses` says which) and nothing is written. A clause's
     * duration id the server does not know is `unknown_package_duration`.
     */
    policy: "/api/v1/policy/",
    /** `POST`, no body. The same as `guidelinesReset`, for the policy. */
    policyReset: "/api/v1/policy/reset/",
  },
  /**
   * The plans the dietitian sells, as the My brand screen's Plans tab
   * lists them.
   *
   * A plan names a duration by its id from `catalog.planDurations` and
   * carries its price as a decimal string - `"2999.00"`, not `2999` - which is
   * how the backend keeps money. Both come back expanded: the duration as the
   * whole `{ id, label }`, the price as the string it was stored as.
   */
  plans: {
    /**
     * `GET` lists the workspace's plans, paginated in DRF's envelope;
     * `POST` creates one and answers with the record it stored.
     */
    list: "/api/v1/packages/",
    /**
     * One plan, by the `public_id` a listed row carries. `GET` reads it,
     * `PATCH` takes only the fields that changed - and both answer with the
     * whole record, in the same shape a row of the list is in.
     *
     * A builder rather than a string, the way `clients.detail` is - so no call
     * site interpolates an id into a URL by hand.
     */
    detail: (publicId: string) =>
      `/api/v1/packages/${encodeURIComponent(publicId)}/`,
    /**
     * Listing a plan on the public page, and taking it off again. `POST`
     * with no body; each answers with the whole record, `is_active` set.
     *
     * Two paths rather than a flag on the `PATCH` above, which is how the
     * backend models it: the state is an action taken on a plan, not a
     * field of it that a form edits.
     */
    activate: (publicId: string) =>
      `/api/v1/packages/${encodeURIComponent(publicId)}/activate/`,
    deactivate: (publicId: string) =>
      `/api/v1/packages/${encodeURIComponent(publicId)}/deactivate/`,
  },
  /**
   * The services the practice offers, as the My brand screen's Services tab
   * lists them.
   *
   * Note the path: `plans` above is `/api/v1/packages/`, and this is
   * `/api/v1/practitioner-services/`. What this app sells as a plan the
   * backend files as a package, so the names here are ours rather than the
   * URL's and a call site says which record it means. See the note over
   * `BrandService`.
   *
   * A service is either curated - the practitioner picked one of the goals
   * from `catalog.goals`, and it comes back with that `{ id, name }` under
   * `goal` - or custom, named in their own words with no goal behind it.
   */
  services: {
    /**
     * `GET` lists them as a bare array, not DRF's envelope, and takes
     * `?is_active=true` to leave out the ones taken down; `POST` creates one
     * and answers with the record it stored.
     */
    list: "/api/v1/practitioner-services/",
    /**
     * One service, by the `public_id` a listed card carries. `GET` reads it;
     * `PATCH` takes the name and the description, which is the whole of what
     * an edit can change - the goal behind a curated service is fixed at
     * creation, and there is no field for it here.
     *
     * Both answer with the record. `PATCH` has been seen to answer without
     * `created_at`, so what comes back is merged over the card in hand rather
     * than replacing it - see `toBrandService`.
     */
    detail: (publicId: string) =>
      `/api/v1/practitioner-services/${encodeURIComponent(publicId)}/`,
    /**
     * Listing a service on the public page, and taking it off again. `POST`
     * with no body; each answers with the whole record, `is_active` set.
     *
     * Two paths rather than a flag on the `PATCH` above, exactly as
     * `plans.activate` is: the state is an action taken on a service, not a
     * field of it that a form edits.
     */
    activate: (publicId: string) =>
      `/api/v1/practitioner-services/${encodeURIComponent(publicId)}/activate/`,
    deactivate: (publicId: string) =>
      `/api/v1/practitioner-services/${encodeURIComponent(
        publicId
      )}/deactivate/`,
  },
  /**
   * The dietitian's own profile, as the Settings screen's General tab shows and
   * edits it. One path, both verbs, and one body shape between them.
   *
   * Two records behind it, the way `brand` above is two endpoints - the person
   * (`practitioner_profile`) and the practice they are reached at
   * (`brand_profile`) - but here the backend joins them, so the screen reads
   * and writes them in one call. The General tab is one form, which is why.
   *
   * There is no id in the path: the workspace is the record, and the bearer
   * token says which workspace.
   */
  settings: {
    /**
     * `GET` reads both halves, wrapped in the key naming each; `PATCH` writes
     * them and answers with the same shape, so one mapping serves both.
     *
     * The `PATCH` is `multipart/form-data` rather than JSON, and a true partial
     * update: only the fields sent are touched, so the app sends only what the
     * form actually changed. `specializations` is the exception to that being
     * additive - it replaces the whole list, so it goes up as every code the
     * profile should end with, one repeated field per code.
     *
     * The photo travels as an id, not as bytes: upload it through
     * `files.upload` with a purpose of `PROFILE_PHOTO` first, then send the
     * `public_id` it answered with as `profile_photo`. It comes back expanded
     * to the whole stored-file record.
     */
    profile: "/api/v1/settings/",
  },
  /**
   * The support calls this workspace has booked — the history under the Support
   * tab's booking form.
   *
   * Its own group rather than a path under `settings` above: that endpoint is
   * the profile a form edits, and this is a diary. No id in the path, like the
   * rest of the workspace's own records; the bearer token says whose calls
   * these are.
   */
  support: {
    /**
     * `GET` answers `{ support_history: [ … ] }` — a bare list inside that
     * envelope, not DRF's page: a practice books support a handful of times a
     * year, and there is no page to ask for.
     *
     * A row is Cal's booking as our backend relays it:
     *
     *   { cal_uid, scheduled_start, scheduled_end, status, meeting_url,
     *     modify_url, cancellation_reason, notes, rescheduled_from }
     *
     * `cal_uid` is Cal's own id for the booking, which is both the row's
     * identity and the value its booker takes to open on this call rather than
     * an empty one — see `BookCallPage`, where it becomes `rescheduleUid`.
     *
     * `status` is Cal's, upper-cased: `SCHEDULED` for a call still ahead,
     * `CANCELLED` for one called off, and `cancellation_reason` carries why
     * where somebody gave one — empty string, not null, where they did not.
     *
     * `notes` is what the booking form put on the booking on the way to Cal,
     * sent back here on the booking it was written for; empty string where the
     * call was booked without it. It is two answers in one field — the reason
     * the form's dropdown offered, a blank line, then the paragraph typed under
     * it — and the only thing in a row that says what the call is about. The
     * history takes it back apart and titles its rows with the first half; see
     * `splitNotes`. A booking made through Cal's own pages carries whatever was
     * typed there, which is a paragraph and not a pair.
     *
     * `rescheduled_from` is the `cal_uid` of the booking this one replaced, and
     * `null` on an original. Cal books a move as a new booking pointing back at
     * the old one and leaves both `SCHEDULED`, so this pointer is the only
     * trace of the move — it is what makes a row read as rescheduled rather
     * than as one nobody has touched. The booking it names is superseded and is
     * not itself in the list. See `supportHistoryService`.
     */
    history: "/api/v1/support-history/",
  },
  files: {
    /**
     * Stores one file against the caller's workspace and hands back the id and
     * URL it can be referred to by.
     *
     * `multipart/form-data`, with `file` (the bytes) and `purpose` - which is
     * what decides where under the workspace the file is filed. The purposes
     * the app sends are the brand screen's two, `BRAND_LOGO` and `PAYMENT_QR`,
     * and the Settings screen's `PROFILE_PHOTO`; see `FilePurpose`.
     */
    upload: "/api/v1/files/",
  },
  /**
   * The backend's reference lists - the fixed sets of rows a form picks from.
   * Each is read-only, the same for everyone, and needs a bearer token like
   * the rest of the API.
   *
   * The client form's lists answer with a bare array of `{ id, name }`. The
   * two the brand's plan dialog reads answer with that array wrapped in a
   * key naming it, and the durations name their text `label` rather than
   * `name` - so those two get their own service rather than sharing
   * `catalogService`'s mapping.
   */
  catalog: {
    /** Salutations: Mr, Mrs, … */
    titles: "/api/v1/titles/",
    /** What a client is signing up to achieve: Weight loss, Weight gain, … */
    goals: "/api/v1/goals/",
    /** How a client eats: Veg, Vegan, … - the Food preference field. */
    dietaryPatterns: "/api/v1/dietary_patterns/",
    /** What a client reacts to: Peanuts, Milk, … - the Food allergies field. */
    allergens: "/api/v1/allergens/",
    /**
     * What a client likes to eat: North Indian, Thai, … - the Favorite
     * cuisines field.
     *
     * A closed list, like the rest: the pre-consultation block takes
     * `favorite_cuisine_ids` as catalog ids, so a cuisine typed rather than
     * picked is one the endpoint has no id for.
     */
    cuisines: "/api/v1/cuisines/",
    /** How active a client is: Active, Semi active, … */
    lifeStyles: "/api/v1/life_styles/",
    /**
     * What a client is being treated for: PCOS, Thyroid, Diabetes, … - the
     * Medical conditions field on the client form's optional half.
     *
     * A closed list, like the four above: the pre-consultation block takes
     * `medical_condition_ids`, so a condition typed rather than picked is one
     * the endpoint has no id for.
     */
    medicalConditions: "/api/v1/medical_conditions/",
    /**
     * Plan names the brand's create-plan dialog offers: Diet
     * Consultation, Meal Planning, … `{ suggestions: [{ id, name }] }`.
     *
     * Suggestions, not a closed set - the dialog lets a dietitian name a
     * plan the list has never heard of, so nothing validates against this.
     */
    planSuggestions: "/api/v1/package-name-suggestions/",
    /**
     * How long a plan runs: 7 Days, 15 Days, 1 Month, …
     * `{ durations: [{ id, label }] }`. This one *is* closed - the dialog
     * offers no way past it.
     */
    planDurations: "/api/v1/package-durations/",
    /**
     * What kind of plan is being written: Regular, Detox, Fasting, …
     * `{ plan_types: [{ id, name }] }`.
     *
     * Closed, like the durations: the create dialog offers no way past it,
     * and `POST /api/v1/meal-plans/` takes the id rather than the name.
     */
    mealPlanTypes: "/api/v1/meal-plan-types/",
    /**
     * What a meal on a plan can be called: Morning Tea, Pre Breakfast, …
     * The plan builder's meal dialog picks from this, and
     * `POST .../meals/` takes the id rather than the name.
     */
    mealSlotTypes: "/api/v1/meal-slot-types/",
    /**
     * What an exercise on a plan can be: Running, Cycling, Yoga, …
     * `{ exercise_types: [{ id, name }] }`.
     *
     * Suggestions rather than a closed set, unlike the meal plan types above.
     * An exercise plan comes back carrying rows with `exercise_type_id: null`
     * and a name of their own — "Garage Stair Climbs" — so the backend stores
     * exercises this list has never heard of, and the dialog offers the same.
     */
    exerciseTypes: "/api/v1/exercise-types/",
    /**
     * What a supplement's dosage is counted in: mg, g, mcg, ml, IU, Tablet,
     * Capsule, Scoop, Sachet. `{ units: [{ id, name }] }`.
     *
     * Closed, unlike the exercise types: a supplement on a plan comes back
     * carrying a `unit_id` as well as a `unit_name`, so a unit this list has
     * never heard of is not something the backend can store.
     */
    supplementUnits: "/api/v1/supplement-units/",
  },
  /**
   * The plans themselves, as the two list screens show them.
   *
   * One endpoint serves both: the plan list passes no `client` and gets the
   * workspace's plans, and a client's Plans tab passes their `public_id` and
   * gets only theirs. Filtering and paging both happen on the server, so
   * neither screen is limited to what it has already loaded.
   */
  /**
   * A client's exercise plans — the Exercise half of their Plans tab.
   *
   * Written as well as read now, bar one: there is still no `DELETE`, so a
   * plan removed here is hidden in the browser rather than deleted. See
   * `exercisePlanStore`.
   */
  exercisePlans: {
    /**
     * `GET` lists them, paginated in DRF's envelope, filtered by `client` (a
     * client's `public_id`) and paged with `page` (1-based) and `page_size` —
     * the same arrangement the meal plan list uses, so a page of the API is a
     * page of the table.
     *
     * A row carries what the plan is called, its client, when it was made and
     * last sent, what is on it by name (`exercise_names`, in order), how many
     * exercises that is (`exercise_count`, and `suggested_exercise_count` for
     * the ones backed by a row of `catalog.exerciseTypes`) and what it comes
     * to — `total_duration`, as `{ hours, minutes }` rather than one figure.
     *
     * Names only: the durations and the catalog ids are on the detail call,
     * so the table names a plan's exercises without opening it and without a
     * call per row.
     *
     * `POST` creates one, from `{ client, exercises }` — the client by
     * `public_id`, and each exercise either as the catalog row it was picked
     * from (`exercise_type_id: 2`) or as a name typed in its place
     * (`exercise_name: "Garage Stair Climbs"`), never both. The backend names
     * the plan and numbers it, and answers with the whole thing, exercises
     * included — so a create is also a read of what it made.
     */
    list: "/api/v1/exercise-plans/",
    /**
     * One plan, whole: a list row's fields plus `exercises` — each with the
     * type it was picked from (`exercise_type_id`, `null` for one typed
     * rather than picked), its name, its length as `duration_hours` and
     * `duration_minutes`, and where it sits on the plan.
     *
     * This is the only place the exercises on a plan come from. `GET` reads
     * it.
     *
     * `PUT` rewrites it, taking the same `{ client, exercises }` a create
     * does. A replace rather than a patch — the plan ends up holding exactly
     * what went up — which is what makes the dialog's Save mean what it looks
     * like it means: the lines on screen are the lines that get stored, the
     * ones deleted from it included. The same arrangement a meal option's
     * rows are saved under.
     *
     * There is no `DELETE`. A plan removed in the browser is hidden there and
     * comes back on a refresh; see `exercisePlanStore`.
     *
     * A builder rather than a string, the way `mealPlans.detail` is — so no
     * call site interpolates an id into a URL by hand.
     */
    detail: (publicId: string) =>
      `/api/v1/exercise-plans/${encodeURIComponent(publicId)}/`,
    /**
     * Sends the plan to the client: `POST` with `{ emails: [...] }` — a list
     * rather than one address, because a plan is often copied to a spouse or a
     * second inbox and the backend takes them in one call rather than one call
     * each. It answers with a `message` and nothing else.
     *
     * A path of its own rather than a field of the plan, the way a meal plan's
     * own send is: sending is an action taken on a plan, not something a form
     * edits.
     */
    send: (publicId: string) =>
      `/api/v1/exercise-plans/${encodeURIComponent(publicId)}/send/`,
    /**
     * The plan as a document — `application/pdf` rather than the JSON the rest
     * of the API speaks, so it is read with `api.getBlob`.
     *
     * The backend renders it because it is the one that knows the dietitian's
     * brand, and so that what the client is emailed and what their dietitian
     * downloads are the same document. Ask for a PDF followed by a wildcard
     * rather than for `application/pdf` alone — see `mealPlans.pdf`, which is
     * where that `Accept` is explained at length.
     */
    pdf: (publicId: string) =>
      `/api/v1/exercise-plans/${encodeURIComponent(publicId)}/pdf/`,
  },
  /**
   * A client's supplement plans — the Supplement half of their Plans tab.
   *
   * Written as well as read now, bar one: there is still no `DELETE`, so a
   * plan made here cannot be removed.
   */
  supplementPlans: {
    /**
     * `GET` lists them, paginated in DRF's envelope, filtered by `client` (a
     * client's `public_id`) and `search` over the plan name, and paged with
     * `page` (1-based) and `page_size`.
     *
     * `search` is the one thing this list has that the exercise list does not
     * — so the Supplement tab's box narrows the whole list rather than the
     * page in hand, the way the meal plan list's already does.
     *
     * A row carries what the plan is called, its `status` (`DRAFT`, and
     * `ACTIVE` for one that has gone to the client — the same pair the meal
     * plans use), its client, when it was made and activated, what is on it by
     * name (`supplement_names`, in order) and how many that is.
     *
     * Names only: the doses, the units and the times are on the detail call,
     * so the table names a plan's supplements without opening it.
     *
     * `POST` creates one, from `{ client, supplements }` — the client by
     * `public_id`, and each supplement with its `name`, `dosage_strength` as
     * typed, `unit` (the catalog row's id, not its name), `frequency`, its
     * `times`, and `days_of_week` only where the frequency is
     * `SPECIFIC_DAYS`. A time is `{ timing, meal_slot_type, time }`, with the
     * meal as a `catalog.mealSlotTypes` id and the clock as `HH:MM`.
     *
     * Note the asymmetry, which `supplementWire` is where it stops: what goes
     * up is `unit` and `meal_slot_type`, and what comes back is `unit_id` and
     * `meal_slot_type_id` beside the names they resolve to.
     *
     * The backend names the plan and numbers it — "Sup/W01/00004" — opens it
     * as a `DRAFT`, and answers with the whole thing, supplements included. So
     * a create is also a read of what it made.
     */
    list: "/api/v1/supplement-plans/",
    /**
     * One plan, whole: a list row's fields plus `supplements` — each with its
     * name, `dosage_strength` as typed, the unit both ways (`unit_id` and
     * `unit_name`), how often it is taken (`frequency`, with `days_of_week`
     * when that is `SPECIFIC_DAYS`), any `notes`, and the `times` it is taken
     * at.
     *
     * A time carries `timing` (`BEFORE` or `AFTER`), the meal it is timed
     * against both ways (`meal_slot_type_id` and `meal_label`), the clock
     * `time` as `HH:MM:SS`, and `sort_order`. Several per supplement, because
     * one taken morning and night is two times on one supplement rather than
     * two supplements.
     *
     * This is the only place the supplements on a plan come from. `GET` reads
     * it.
     *
     * `PUT` rewrites it, taking the same `{ client, supplements }` a create
     * does. A replace rather than a patch — the whole list goes up and the
     * plan ends up holding exactly that, the supplements deleted from it
     * included — which is what makes the screen's Save mean what it looks like
     * it means. The same arrangement an exercise plan's own update makes.
     */
    detail: (publicId: string) =>
      `/api/v1/supplement-plans/${encodeURIComponent(publicId)}/`,
    /**
     * Sends the plan to the client: `POST` with `{ emails: [...] }` — a list
     * rather than one address, because a plan is often copied to a spouse or a
     * second inbox and the backend takes them in one call rather than one call
     * each. It answers with a `message` and nothing else.
     *
     * A path of its own rather than a field of the plan, the way a meal plan's
     * and an exercise plan's own sends are: sending is an action taken on a
     * plan, not something a form edits.
     */
    send: (publicId: string) =>
      `/api/v1/supplement-plans/${encodeURIComponent(publicId)}/send/`,
    /**
     * The plan as a document — `application/pdf` rather than the JSON the rest
     * of the API speaks, so it is read with `api.getBlob`.
     *
     * The backend renders it because it is the one that knows the dietitian's
     * brand, and so that what the client is emailed and what their dietitian
     * downloads are the same document. Ask for a PDF followed by a wildcard
     * rather than for `application/pdf` alone — see `mealPlans.pdf`, which is
     * where that `Accept` is explained at length.
     */
    pdf: (publicId: string) =>
      `/api/v1/supplement-plans/${encodeURIComponent(publicId)}/pdf/`,
  },
  mealPlans: {
    /**
     * `GET` lists them, paginated in DRF's envelope, with three optional
     * filters: `client` (a client's `public_id`), `status` (`ACTIVE` for a
     * plan that has gone to the client, `DRAFT` for one still being written),
     * and `search` over the plan name. Leaving `status` off is what asks for
     * both - which is the All tab.
     *
     * The page itself is `page` (1-based) and `page_size`, the way the client
     * list is paged - both tables ask for ten, so a page of the API is a page
     * of the table. `count` in the envelope is everything the filters match,
     * which is what the page control is drawn from.
     *
     * `POST` creates one, from `{ client, plan_type, name }`. The two
     * references go up as ids the way the client form's do - `plan_type: 1`,
     * not `plan_type: "Regular"` - and `plan_type` comes back expanded to its
     * name. The backend assigns the `plan_code` and opens the plan as a
     * `DRAFT` with no meals on it.
     */
    list: "/api/v1/meal-plans/",
    /**
     * One plan, whole: the fields a list row carries plus the day itself -
     * meals, the options under each, and the food rows under those, every
     * one with the nutrition it was priced at.
     *
     * This is the only place a plan's meals come from. `GET` reads it.
     *
     * `PATCH` renames it, from `{ name }` alone - the one field of a plan
     * itself that is edited after it is created, and only while it is still a
     * draft. Everything else the builder writes belongs to a meal, an option
     * or a row underneath, each with its own path below.
     *
     * A partial update rather than the `POST` the spec named: posting here
     * answers 405, so the detail route serves the verbs a DRF detail route
     * serves, and one field of a plan is a `PATCH` of it.
     *

     * A builder rather than a string, the way `clients.detail` is - so no
     * call site interpolates an id into a URL by hand.
     */
    detail: (publicId: string) =>
      `/api/v1/meal-plans/${encodeURIComponent(publicId)}/`,
    /**
     * Sends the plan to the client: `POST` with no body, answering with the
     * whole plan, now `ACTIVE` and carrying the `activated_at` it was sent
     * at.
     *
     * A path of its own rather than a `PATCH` of `status`, the way a
     * plan's own activate is - the state is an action taken on a plan, not
     * a field of it that a form edits.
     */
    activate: (publicId: string) =>
      `/api/v1/meal-plans/${encodeURIComponent(publicId)}/activate/`,
    /**
     * The plan as a document. `GET` answers with the PDF itself - the bytes,
     * not a link to them - so it is read with `api.getBlob` and an `Accept` of
     * `application/pdf` rather than the JSON the rest of the API speaks.
     *
     * The backend renders it, headed with the dietitian's own brand, which is
     * why the app does not build one: what the client is emailed and what the
     * dietitian downloads are then the same document.
     */
    pdf: (publicId: string) =>
      `/api/v1/meal-plans/${encodeURIComponent(publicId)}/pdf/`,
    /**
     * Emails the plan out. `POST` with `{ emails: ["one@example.com", …] }` -
     * a list, because a plan is often copied to a spouse or a second inbox,
     * and it answers with the addresses it accepted.
     *
     * The document that goes out is the same one `pdf` above serves; the
     * addresses are the only thing this call decides.
     */
    send: (publicId: string) =>
      `/api/v1/meal-plans/${encodeURIComponent(publicId)}/send/`,
    /**
     * The meals on one plan. `POST` adds one from
     * `{ meal_slot_type, time_of_day }` - the type as the id its catalog row
     * carries, the time as `HH:MM` - and answers with the meal itself, the
     * backend having decided its `id` and `sort_order`.
     */
    meals: (publicId: string) =>
      `/api/v1/meal-plans/${encodeURIComponent(publicId)}/meals/`,
    /**
     * One meal on it, by the `id` the meal carries. `PATCH` takes either of
     * the two fields above, or both, and answers with the whole meal in the
     * same shape `POST` does; `DELETE` takes it off the plan, and with it
     * every option and food row underneath.
     */
    meal: (publicId: string, slotId: string | number) =>
      `/api/v1/meal-plans/${encodeURIComponent(publicId)}/meals/${encodeURIComponent(
        String(slotId)
      )}/`,
    /**
     * The options under one meal - the ways of eating it the client picks
     * between. `POST` adds one from `{ name, sort_order }`: what the page
     * calls it ("Option 1") and where it sits among the rest.
     *
     * It answers with the option itself - the `id` the backend gave it, its
     * totals, and the food rows on it, which is none to begin with.
     *
     * A meal comes back carrying no options at all and the builder always
     * shows one blank option to write in, so the first `POST` under a meal is
     * usually that one, created the moment something needs it to be real.
     */
    options: (publicId: string, slotId: string | number) =>
      `/api/v1/meal-plans/${encodeURIComponent(publicId)}/meals/${encodeURIComponent(
        String(slotId)
      )}/options/`,
    /**
     * One option on a meal. `DELETE` takes it off, and with it every food row
     * on it. There is no `PATCH`: an option holds nothing of its own that a
     * form edits - its rows are their own call, below.
     *
     * Under the plan, like the `POST` that creates one. The flat
     * `/api/v1/meals/{slot}/options/{id}/` the spec named answers Django's
     * own HTML 404 rather than DRF's JSON one, which is a route that was
     * never registered rather than an option that is not there.
     */
    option: (
      publicId: string,
      slotId: string | number,
      optionId: string | number
    ) =>
      `/api/v1/meal-plans/${encodeURIComponent(publicId)}/meals/${encodeURIComponent(
        String(slotId)
      )}/options/${encodeURIComponent(String(optionId))}/`,
    /**
     * The food rows on one option. `PUT` writes the list whole, from
     * `{ items: [{ food_id, unit_id, quantity }] }` - the food and the unit as
     * the ids the food catalog handed out, the quantity as a decimal string
     * ("2.00"), which is how the backend keeps it.
     *
     * A replace, not an append: the option ends up holding exactly what went
     * up, so a row taken out of the table on screen is a row taken off the
     * option. Which is what lets the builder's Save button mean what it looks
     * like it means - the table as it stands is the table that is stored.
     *
     * It answers with `{ items: [...] }`, each row priced the way the plan's
     * own rows arrive: the ids to ask the food API about again, and the
     * nutrition the row was stored at.
     */
    optionItems: (
      publicId: string,
      slotId: string | number,
      optionId: string | number
    ) =>
      `/api/v1/meal-plans/${encodeURIComponent(publicId)}/meals/${encodeURIComponent(
        String(slotId)
      )}/options/${encodeURIComponent(String(optionId))}/items/`,
  },
  /**
   * How many plans of each kind the workspace holds - the three totals across
   * the top of the Create plan screen.
   *
   * `GET` answers `{ total_meal_plans, total_supplement_plans,
   * total_exercise_plans }`, whole numbers and nothing else. One call for all
   * three, where the screen used to ask each list for a page of one row and
   * read the `count` off its envelope.
   */
  planCounts: "/api/v1/plans/counts/",
  /**
   * The food database a meal option's rows are built from - search, its
   * per-food units, the nutrient profile of a quantity in one of them, and
   * the unit list on its own.
   *
   * All four need the bearer token like the rest of the API, but nothing
   * about a workspace: the catalog is the same for every dietitian.
   */
  foods: {
    /**
     * `GET`, matched case-insensitively against `q` and paginated in DRF's
     * envelope - `page` and `page_size`. An empty `q` serves the catalog
     * unfiltered, which is what the Dish field opens on before anything is
     * typed into it.
     */
    list: "/api/v1/foods/",
    /**
     * What one food can be measured in - `{ units: [{ id, name }] }`. Read
     * once a food is picked, since the Unit field has nothing to offer before
     * that.
     */
    units: (foodId: number) => `/api/v1/foods/${foodId}/units/`,
    /**
     * The nutrient profile of `quantity` of the food in the unit `unitId`
     * names, both passed as query params. What the row's macro columns are
     * filled from once a dish has a unit and a quantity.
     */
    nutrition: (foodId: number) => `/api/v1/foods/${foodId}/nutrition/`,
    /**
     * Every unit the catalog measures anything in - `{ units: [{ id, name }] }`,
     * from "gm" to "Scoop", and no food to hang them off.
     *
     * What a dish written out by hand is measured in: there is no food behind
     * such a row to ask `units` about, but the unit it is served in is still
     * one of these. No conversions come with them - a custom row says what one
     * of its unit weighs itself.
     */
    unitCatalog: "/api/v1/food-units/",
    /**
     * Which dishes `list` searches for this workspace - its own alone, or its
     * own and the shared catalog. `GET` reads it, `PUT { mode }` changes it,
     * and both answer `{ mode, available: [{ value, label }] }`.
     *
     * One setting for the whole workspace rather than per plan, which is why
     * the plan builder's switch says the choice is kept across every plan.
     */
    catalogPreference: "/api/v1/catalog-preference/",
  },
  /**
   * The dishes a workspace has of its own - the Dishes screen's whole list.
   *
   * A sibling of `foods` above rather than a filter on it: that one is the
   * shared catalog every dietitian searches, and this one is what a single
   * practice has written or uploaded for itself. The rows come back with their
   * nutrition already on them, which is the difference that matters here - the
   * catalog makes you ask for a food's macros a quantity at a time, and a list
   * of twenty dishes cannot make twenty calls to fill four columns.
   */
  workspaceFoods: {
    /**
     * `GET`, paginated in DRF's envelope - `page` and `page_size`, and nothing
     * else. There is no search param on it yet, which is why the Dishes
     * screen's box narrows the page it is holding rather than asking the
     * server; see `useDishes`.
     */
    list: "/api/v1/workspace-foods/",
    /**
     * The blank workbook a bulk upload is written into - `GET`, answering with
     * the file itself rather than a link to it, so it is read with
     * `api.getBlob` and an `Accept` of the spreadsheet type. The dialog's
     * "Download excel template" is the only thing that asks for it.
     */
    importTemplate: "/api/v1/workspace-foods/import/template/",
    /**
     * That workbook sent back filled in - `POST`, `multipart/form-data`, the
     * sheet under `file`. What the dialog's Import does.
     *
     * A 200 here does not mean every row landed: the answer is a tally of what
     * was imported, updated and refused, a reason per refused row, and those
     * rows again as a workbook to fix and send back. See `DishImportResponse`.
     */
    import: "/api/v1/workspace-foods/import/",
  },
} as const;
